<template>
  <div class="template-list">
    <!-- Header com filtros e botão criar -->
    <div class="list-header">
      <div class="filtros">
        <input
          v-model="filtros.search"
          type="text"
          placeholder="Buscar templates..."
          class="input-busca"
          @input="debouncedCarregar"
        />
        <select v-model="filtros.canal" class="select-filtro" @change="carregarLista">
          <option value="">Todos os canais</option>
          <option value="email">Email</option>
          <option value="whatsapp">WhatsApp</option>
          <option value="ambos">Ambos</option>
        </select>
        <select v-model="filtros.ativo" class="select-filtro" @change="carregarLista">
          <option value="">Todos</option>
          <option value="true">Ativos</option>
          <option value="false">Inativos</option>
        </select>
      </div>
      <button class="btn-criar" @click="$emit('criar')">
        <i class="fas fa-plus"></i> Novo Template
      </button>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="loading-container">
      <div class="spinner"></div>
      <span>Carregando templates...</span>
    </div>

    <!-- Lista vazia -->
    <div v-else-if="templates.length === 0" class="empty-state">
      <i class="fas fa-file-alt"></i>
      <p>Nenhum template encontrado</p>
      <button class="btn-criar-empty" @click="$emit('criar')">
        Criar primeiro template
      </button>
    </div>

    <!-- Lista de templates -->
    <div v-else class="templates-grid">
      <div
        v-for="template in templates"
        :key="template.id"
        class="template-card"
        :class="{ inativo: !template.ativo }"
      >
        <div class="card-header">
          <span class="canal-badge" :class="template.canal">
            <i :class="getIconeCanal(template.canal)"></i>
            {{ getCanalLabel(template.canal) }}
          </span>
          <span v-if="template.usar_ia" class="ia-badge" title="Usa IA para gerar conteúdo">
            <i class="fas fa-robot"></i> IA
          </span>
        </div>

        <h3 class="template-nome">{{ template.nome }}</h3>

        <p v-if="template.assunto" class="template-assunto">
          <strong>Assunto:</strong> {{ template.assunto }}
        </p>

        <div class="template-preview" v-html="previewHtml(template.corpo, 120)"></div>

        <div class="card-footer">
          <span class="status-badge" :class="{ ativo: template.ativo, inativo: !template.ativo }">
            {{ template.ativo ? 'Ativo' : 'Inativo' }}
          </span>
          <div class="card-actions">
            <button class="btn-icon" title="Visualizar" @click="$emit('preview', template)">
              <i class="fas fa-eye"></i>
            </button>
            <button class="btn-icon" title="Editar" @click="$emit('editar', template)">
              <i class="fas fa-edit"></i>
            </button>
            <button class="btn-icon btn-danger" title="Excluir" @click="confirmarExclusao(template)">
              <i class="fas fa-trash"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import Swal from 'sweetalert2'

const props = defineProps({
  templates: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
})

const emit = defineEmits(['criar', 'editar', 'excluir', 'preview', 'carregar'])

const filtros = ref({
  search: '',
  canal: '',
  ativo: '',
})

let debounceTimer = null

function debouncedCarregar() {
  clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => {
    carregarLista()
  }, 300)
}

function carregarLista() {
  emit('carregar', filtros.value)
}

function getIconeCanal(canal) {
  const icones = {
    email: 'fas fa-envelope',
    whatsapp: 'fab fa-whatsapp',
    ambos: 'fas fa-bullhorn',
  }
  return icones[canal] || 'fas fa-bell'
}

function getCanalLabel(canal) {
  const labels = {
    email: 'Email',
    whatsapp: 'WhatsApp',
    ambos: 'Ambos',
  }
  return labels[canal] || canal
}

function truncateText(text, maxLength) {
  if (!text) return ''
  if (text.length <= maxLength) return text
  return text.substring(0, maxLength) + '...'
}

function previewHtml(html, maxLength) {
  if (!html) return ''
  // Criar elemento temporário para extrair texto e preservar formatação básica
  const temp = document.createElement('div')
  temp.innerHTML = html
  // Extrair texto puro para calcular truncamento
  const textoPlano = temp.textContent || ''
  if (textoPlano.length <= maxLength) return html
  // Truncar: usar texto visual limitado
  // Remover tags pesadas mas manter inline
  let resultado = html
    .replace(/<\/p>/gi, ' ')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/?(div|p|h[1-6]|blockquote|pre|table|tr|td|th)\b[^>]*>/gi, '')
  // Strip tags para truncar corretamente
  const tempTrunc = document.createElement('div')
  tempTrunc.innerHTML = resultado
  const textoLimpo = tempTrunc.textContent || ''
  const truncado = textoLimpo.substring(0, maxLength)
  return truncado + '...'
}

async function confirmarExclusao(template) {
  const result = await Swal.fire({
    title: 'Excluir template?',
    text: `O template "${template.nome}" será excluído permanentemente.`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#dc3545',
    cancelButtonColor: '#6c757d',
    confirmButtonText: 'Sim, excluir',
    cancelButtonText: 'Cancelar',
  })

  if (result.isConfirmed) {
    emit('excluir', template)
  }
}

onMounted(() => {
  carregarLista()
})
</script>

<style scoped>
.template-list {
  padding: 20px;
}

.list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 15px;
}

.filtros {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.input-busca {
  padding: 10px 15px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  min-width: 200px;
}

.select-filtro {
  padding: 10px 15px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  background: white;
  cursor: pointer;
}

.btn-criar {
  padding: 10px 20px;
  background: #4caf50;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background 0.2s;
}

.btn-criar:hover {
  background: #388e3c;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px;
  color: #666;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #4caf50;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #666;
}

.empty-state i {
  font-size: 48px;
  margin-bottom: 15px;
  color: #ccc;
}

.btn-criar-empty {
  margin-top: 15px;
  padding: 10px 20px;
  background: #4caf50;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.templates-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px;
}

.template-card {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  padding: 20px;
  transition: box-shadow 0.2s, transform 0.2s;
}

.template-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.template-card.inativo {
  opacity: 0.7;
  background: #f9f9f9;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.canal-badge {
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 5px;
}

.canal-badge.email {
  background: #e3f2fd;
  color: #1976d2;
}

.canal-badge.whatsapp {
  background: #e8f5e9;
  color: #388e3c;
}

.canal-badge.ambos {
  background: #fff3e0;
  color: #f57c00;
}

.ia-badge {
  padding: 4px 8px;
  background: #f3e5f5;
  color: #7b1fa2;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
}

.template-nome {
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 8px 0;
  color: #333;
}

.template-assunto {
  font-size: 13px;
  color: #666;
  margin: 0 0 8px 0;
}

.template-preview {
  font-size: 13px;
  color: #888;
  line-height: 1.5;
  margin: 0 0 15px 0;
  min-height: 40px;
  max-height: 60px;
  overflow: hidden;
  word-break: break-word;
}

.card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 12px;
  border-top: 1px solid #eee;
}

.status-badge {
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
}

.status-badge.ativo {
  background: #e8f5e9;
  color: #388e3c;
}

.status-badge.inativo {
  background: #f5f5f5;
  color: #757575;
}

.card-actions {
  display: flex;
  gap: 8px;
}

.btn-icon {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  background: #f5f5f5;
  color: #666;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s, color 0.2s;
}

.btn-icon:hover {
  background: #e0e0e0;
  color: #333;
}

.btn-icon.btn-danger:hover {
  background: #ffebee;
  color: #c62828;
}

/* Dark mode */
.dark-mode .template-card {
  background: #2a2a2a;
  border-color: #444;
}

.dark-mode .template-card.inativo {
  background: #1e1e1e;
}

.dark-mode .template-nome {
  color: #f1f1f1;
}

.dark-mode .input-busca,
.dark-mode .select-filtro {
  background: #2a2a2a;
  border-color: #444;
  color: #f1f1f1;
}

@media (max-width: 768px) {
  .list-header {
    flex-direction: column;
    align-items: stretch;
  }

  .filtros {
    flex-direction: column;
  }

  .input-busca,
  .select-filtro {
    width: 100%;
  }

  .btn-criar {
    width: 100%;
    justify-content: center;
  }

  .templates-grid {
    grid-template-columns: 1fr;
  }
}
</style>
